import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { finalize, Subject, takeUntil } from 'rxjs';

import { OrganizationDto } from 'src/app/modules/organizations/dtos/organization';
import { OrganizationsService } from 'src/app/modules/organizations/organizations.service';
import { SnackBarService } from 'src/app/shared/components/snack-bar/snack-bar.service';

@Component({
	selector: 'app-security',
	styleUrls: ['./security.component.scss'],
	templateUrl: './security.component.html',
})
export class SecurityComponent implements OnInit, OnDestroy {
	isLoading = false;
	isLoadingPasswordExpiration = false;
	isPasswordExpirationEnabled = false;
	isTwoFactorEnabled = false;
	organizationId: string = '';

	private readonly unsubscribe$: Subject<void> = new Subject<void>();

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly organizationsService: OrganizationsService,
		private readonly snackBarService: SnackBarService,
	) {}

	fetchOrganizationDetails(): void {
		this.isLoading = true;
		this.organizationsService
			.getById(this.organizationId)
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => (this.isLoading = false)),
			)
			.subscribe({
				error: (err: HttpErrorResponse) => {
					this.snackBarService.showSnack(
						err.error?.message || 'Erro ao buscar detalhes da organização.',
						'error',
					);
				},
				next: (org: OrganizationDto) => {
					this.isTwoFactorEnabled = org.twoFactorAuth || false;
					this.isPasswordExpirationEnabled = org.passwordExpires || false;
				},
			});
	}

	getButtonText(): string {
		return this.isTwoFactorEnabled
			? 'Desativar Autenticação de Dois Fatores'
			: 'Ativar Autenticação de Dois Fatores';
	}

	getPasswordExpirationButtonText(): string {
		return this.isPasswordExpirationEnabled
			? 'Desativar Expiração de Senha'
			: 'Ativar Expiração de Senha';
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	ngOnInit(): void {
		this.activatedRoute.params
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((params) => {
				this.organizationId = params.id;
				if (this.organizationId) {
					this.fetchOrganizationDetails();
				}
			});
	}

	togglePasswordExpiration(): void {
		if (!this.organizationId) {
			this.snackBarService.showSnack(
				'ID da organização não encontrado.',
				'error',
			);

			return;
		}

		this.isLoadingPasswordExpiration = true;
		this.organizationsService
			.togglePasswordExpiration(this.organizationId)
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => (this.isLoadingPasswordExpiration = false)),
			)
			.subscribe({
				error: (err: HttpErrorResponse) => {
					this.snackBarService.showSnack(
						err.error?.message ||
							'Erro ao atualizar status de expiração de senha.',
						'error',
					);
				},
				next: (response) => {
					this.isPasswordExpirationEnabled = !this.isPasswordExpirationEnabled;
					this.snackBarService.showSnack(
						response.message ||
							'Status de expiração de senha atualizado com sucesso.',
						'success',
					);
				},
			});
	}

	toggleTwoFactor(): void {
		if (!this.organizationId) {
			this.snackBarService.showSnack(
				'ID da organização não encontrado.',
				'error',
			);

			return;
		}

		this.isLoading = true;
		this.organizationsService
			.toggleTwoFactorAuth(this.organizationId)
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => (this.isLoading = false)),
			)
			.subscribe({
				error: (err: HttpErrorResponse) => {
					this.snackBarService.showSnack(
						err.error?.message || 'Erro ao atualizar status 2FA.',
						'error',
					);
				},
				next: (response) => {
					this.isTwoFactorEnabled = !this.isTwoFactorEnabled;
					this.snackBarService.showSnack(
						response.message || 'Status 2FA atualizado com sucesso.',
						'success',
					);
				},
			});
	}
}
